// import { Exercises } from "../../../constants/constants";
// import { SERVER_BASE_URL } from "../../../constants/domain";
import VideoInPerformance from '../video-in-performance';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import 'twin.macro';
import { useReferenceVideoStatus } from '../../../zustand/reference-video-status-store';
import { VIDEO_STATE } from '../../../features/exercise/connection-status';
import {
  SpinnerIcon,
  VideoOffIcon,
} from '../../../features/exercise/components/connection-status-icons';
// Temporary developer-only observability (network connectivity investigation,
// Step 3). A no-op unless FEATURES.NETWORK_DIAGNOSTICS — see the store for the full rationale.
import { useNetworkDiagnostics } from '../../../zustand/network-diagnostics-store';

const VideosSections = ({
  videoSrc,
  version,
  isRightSide,
  videoPlayerRef,
  inPerformance,
  compact,
  isStackedMobile,
  // Patient-facing playback state from use-connection-status (VIDEO_STATE), or
  // undefined where no status is wanted.
  videoStatus,
}) => {
  const { t } = useTranslation('exercise');
  const NoRepeatedVideoRef = useRef();
  const markWaiting = useReferenceVideoStatus(state => state.markWaiting);
  const markPlaying = useReferenceVideoStatus(state => state.markPlaying);
  const markError = useReferenceVideoStatus(state => state.markError);
  const clearWaiting = useReferenceVideoStatus(state => state.clearWaiting);
  const resetVideoStatus = useReferenceVideoStatus(state => state.reset);
  const recordNetworkMetric = useNetworkDiagnostics(state => state.record);
  const waitingSinceRef = useRef(null);

  useEffect(() => {
    if (inPerformance && NoRepeatedVideoRef?.current) {
      // this code for looping and auto play when the movex is not sponge or shutdown or yoga
      NoRepeatedVideoRef.current.loop = true;
      NoRepeatedVideoRef.current.play();
    } else if (!inPerformance) {
      if (NoRepeatedVideoRef?.current) {
        NoRepeatedVideoRef.current.loop = false;
        NoRepeatedVideoRef.current?.pause();
        NoRepeatedVideoRef.current.currentTime = 0;
      } else if (videoPlayerRef?.current) {
        videoPlayerRef.current.loop = false;
        videoPlayerRef.current?.pause();
        videoPlayerRef.current.currentTime = 0;
      }
    }
  }, [inPerformance, NoRepeatedVideoRef?.current]);

  // A new source starts clean — an error on the previous one says nothing about it.
  useEffect(() => {
    resetVideoStatus();
    return resetVideoStatus;
  }, [videoSrc, resetVideoStatus]);

  // Local only: re-runs the element's own load of the same src, exactly as the
  // browser does on first mount. No new request type, no URL change.
  const retryVideo = useCallback(() => {
    const video = NoRepeatedVideoRef.current;

    if (!video) {
      return;
    }

    resetVideoStatus();
    video.load();

    if (inPerformance) {
      video.play()?.catch?.(() => {});
    }
  }, [inPerformance, resetVideoStatus]);

  return (
    <>
      <VideoInPerformance
        src={videoSrc}
        isRightSide={isRightSide}
        autoPlay={inPerformance}
        loop={true}
        videoPlayerRef={NoRepeatedVideoRef}
        compact={compact}
        isStackedMobile={isStackedMobile}
        // Diagnostics only (network connectivity investigation, Step 3) —
        // forwarded straight onto the native <video> via VideoInPerformance's
        // existing {...rest} spread, no change to that component.
        onWaiting={() => {
          markWaiting();
          waitingSinceRef.current = performance.now();
          recordNetworkMetric('media', 'video_waiting', 1, 'count', {
            endpoint: videoSrc,
            userVisibleImpact: 'Video buffering',
          });
        }}
        onPlaying={() => {
          markPlaying();
          if (waitingSinceRef.current !== null) {
            recordNetworkMetric(
              'media',
              'video_buffering_duration_ms',
              Math.round(performance.now() - waitingSinceRef.current),
              'ms',
              { endpoint: videoSrc }
            );
            waitingSinceRef.current = null;
          }
        }}
        // A paused or ready element is not buffering in any way the patient sees.
        onPause={clearWaiting}
        onCanPlay={markPlaying}
        onError={() => {
          // An empty src (before the intro payload names the video) is not a failure.
          if (videoSrc) {
            markError();
          }
          recordNetworkMetric('media', 'video_error', 1, 'count', {
            endpoint: videoSrc,
            userVisibleImpact: 'Media failed to load',
          });
        }}
      />
      {videoStatus === VIDEO_STATE.loading && (
        <div
          role="status"
          tw="absolute bottom-3 right-3 z-[20] flex items-center gap-2 rounded-full bg-white/95 py-2 pl-2.5 pr-3.5 text-[13px] font-medium text-text_primary shadow-[0_4px_14px_rgba(0,0,0,0.12)] rtl:right-auto rtl:left-3"
        >
          <SpinnerIcon tw="h-[18px] w-[18px] shrink-0 animate-spin text-text_tertiary motion-reduce:animate-none" />
          {t('connectionStatus.video.loading')}
        </div>
      )}
      {videoStatus === VIDEO_STATE.error && (
        <div
          role="status"
          tw="absolute inset-0 z-[20] flex flex-col items-center justify-center gap-3 bg-[#E9ECE4] px-6 text-center"
        >
          <span tw="flex h-11 w-11 items-center justify-center rounded-full bg-white">
            <VideoOffIcon tw="h-[22px] w-[22px] text-[#3F5670]" />
          </span>
          <p tw="m-0 text-sm font-semibold text-text_primary">
            {t('connectionStatus.video.error')}
          </p>
          <button
            type="button"
            onClick={retryVideo}
            tw="min-h-[44px] rounded-[10px] bg-primary px-[18px] text-[13px] font-semibold text-white"
          >
            {t('connectionStatus.video.retry')}
          </button>
        </div>
      )}
    </>
  );
};

export default VideosSections;
